<template>
  <div class="card white dish-card">
    <div class="menu-item-image-container">
      <img :src="mediaSrc(recipe.photo)" class="menu-item-image" />
      <div v-if="recipe.rate !== 0" class="menu-item-rate">{{ recipe.rate }}</div>
    </div>

    <div class="dish-main">
      <div class="dish-header">
        <div>
          <div class="dish-type">{{ recipe.dish_type_display }}</div>
          <div class="dish-title">{{ displayRecipeName }}</div>
        </div>
        <div class="dish-actions">
          <button v-if="hasMenuItemContext" class="replace-button" @click.stop="showReplaceModal = true">
            <font-awesome-icon class="icon" :icon="['fas', 'arrows-rotate']" />
            Remplacer ce plat
          </button>
          <LikeBtn :value="recipe.liked" @toggle="toggleLike" />
          <SocialSharingModal :dish="recipe" />
        </div>
      </div>

      <div class="quick-info">
        <div class="info-chip"><font-awesome-icon icon="utensils" /><span><small>Difficulté</small>{{ recipe.difficulty }}</span></div>
        <div class="info-chip"><font-awesome-icon icon="clock" /><span><small>Préparation</small>{{ recipe.preparation_time }} min</span></div>
        <div class="info-chip"><span><small>Budget</small>{{ recipe.price }}</span></div>
        <div class="info-chip"><span><small>Profil glycémique</small>{{ glycemicIndexLabel(recipe.glycemic_index) }}</span></div>
      </div>

      <div class="macro-profile" v-if="macroLabels.length">
        <div>
          <strong>Profil nutritionnel du plat</strong>
          <p>Ce profil aide Mes Meilleurs Menus à associer des plats complémentaires au sein du repas.</p>
        </div>
        <div class="macro-badges">
          <span v-for="macro in macroLabels" :key="macro">✓ {{ macro }}</span>
        </div>
      </div>

      <div class="quantity-context">
        <strong>Mes quantités personnalisées</strong>
        <span v-if="householdSize">Quantités totales calculées pour {{ householdSize }} {{ householdSize > 1 ? "personnes" : "personne" }}.</span>
        <span v-else>Quantités calculées pour les personnes prévues à ce repas.</span>
      </div>

      <div v-if="portionDistribution.length > 1" class="portion-card">
        <div class="portion-copy">
          <strong>Comment répartir ce plat ?</strong>
          <span>Part indicative de chacun selon ses besoins énergétiques.</span>
          <div class="portion-legend">
            <div v-for="(member, index) in portionDistribution" :key="member.id" class="portion-person">
              <i :style="{ background: portionColors[index % portionColors.length] }"></i>
              <b>{{ member.name }}</b> {{ Math.round(Number(member.share) || 0) }} %
            </div>
          </div>
        </div>
        <div class="portion-pie" :style="{ background: pieBackground }"></div>
      </div>

      <details v-if="personalizedIngredients.length" class="personalized-breakdown">
        <summary>Voir les quantités par personne</summary>
        <div class="personalized-grid">
          <div v-for="member in personalizedIngredients" :key="member.consumer_id" class="personalized-member">
            <div class="personalized-member-title">
              <strong>{{ member.consumer_name }}</strong>
              <span v-if="member.sport_load && member.sport_load !== 'rest'">MMM Sport · {{ sportLoadLabel(member.sport_load) }}</span>
            </div>
            <ul>
              <li v-for="(ing, index) in member.ingredients" :key="index">
                {{ ing.name }} : {{ ing.quantity }} {{ ing.unit }}
              </li>
            </ul>
            <div v-if="member.supplement" class="sport-supplement">
              <strong>Complément sportif</strong>
              <span>+ {{ member.supplement.quantity }} {{ member.supplement.unit }} de {{ member.supplement.name }}</span>
              <small v-if="member.supplement.quantity_basis === 'dry'">Poids sec avant cuisson</small>
              <small v-else-if="member.supplement.quantity_basis === 'raw'">Poids cru avant cuisson</small>
              <small v-else-if="member.supplement.quantity_basis === 'cooked'">Poids cuit / prêt à servir</small>
              <small v-else>Poids tel que servi</small>
              <small>≈ {{ member.supplement.carbohydrate_g }} g de glucides supplémentaires</small>
              <small v-if="member.lipid_kcal_reduced_for_sport > 0">Rééquilibrage : une partie des lipides du plat a été réduite pour garder un apport énergétique cohérent.</small>
            </div>
          </div>
        </div>
      </details>

      <div class="menu-item-ingredients">
        <ul>
          <li v-for="(ing, index) in ingredients" :key="index">
            {{ ingredientName(ing) }} : {{ ing.quantity }} {{ ing.unit }}
          </li>
        </ul>
      </div>

      <RecipePreparation class="menu-item-recipe" :steps="recipe.recipe_steps || []" :text="recipe.recipe || ''" />

      <div style="display:flex; justify-content:space-between; margin-top:2rem; margin-bottom:1rem;">
        <div class="menu-item-name">
          <span v-if="recipe.my_comment">Votre commentaire</span>
          <span v-else>Soyez le premier à écrire un commentaire !</span>
        </div>

        <div>
          <font-awesome-icon
            v-for="i in 5"
            :key="i"
            :icon="getReviewStarIcon(i, recipe.my_rate)"
            @mouseover="hoverStar(i)"
            @mouseleave="resetHover"
            @click="selectStar(i)"
            class="star"
          />
        </div>
      </div>

      <div v-if="reviewEditionMode" style="margin-top:10px;">
        <textarea class="form-input" v-model="myReview" rows="3" placeholder="Votre commentaire" />
        <div style="margin-top:10px; text-align:right;">
          <button v-if="recipe.my_comment" class="button gray" @click="reviewEditionMode = false">Annuler</button>
          <button class="button" @click="saveMyReview">Envoyer</button>
        </div>
      </div>

      <div v-else class="review">
        <div>{{ recipe.my_comment.comment }}</div>
        <button class="button gray" @click="editReview">Modifier</button>
      </div>
    </div>

    <ReplaceDishModal
      v-if="showReplaceModal && hasMenuItemContext"
      :item="item"
      :date="dateFromRoute"
      @close="showReplaceModal = false"
      @update="updateRecipe"
    />
  </div>
</template>

<script>
import SocialSharingModal from "@/components/SocialSharingModal.vue";
import LikeBtn from "@/components/LikeBtn.vue";
import ReplaceDishModal from "@/components/ReplaceDishModal.vue";
import { api } from "@/api";
import RecipePreparation from "@/components/RecipePreparation.vue";

export default {
  name: "DishCard",
  components: { SocialSharingModal, LikeBtn, ReplaceDishModal, RecipePreparation },

  props: {
    recipe: { type: Object, required: true },
    item: { type: Object, default: null },
    adjustedIngredients: { type: Array, default: null },
    peopleCount: { type: Number, default: null },
  },

  data() {
    return {
      hoveredStar: 0,
      myReview: "",
      reviewEditionMode: !this.recipe.my_comment,
      showReplaceModal: false,
    };
  },

  computed: {
    displayRecipeName() {
      const name = String(this.recipe?.name || "").trim();
      const isSnack = ["snack", "morning_snack"].includes(this.item?.dish_type) || ["snack", "morning_snack"].includes(this.recipe?.dish_type);
      if (!isSnack) return name;

      const withoutQuantity =
        name.replace(/^\s*\d+(?:[.,]\d+)?\s*(?:x|×)?\s*/i, "").trim() || name;

      // La quantité réelle est calculée pour le foyer et affichée plus bas.
      // « Poire(s) » est donc plus juste que « 1 Poire ».
      if (/^\d/.test(name) && !/\(s\)$/i.test(withoutQuantity)) {
        return `${withoutQuantity}(s)`;
      }
      return withoutQuantity;
    },
    hasMenuItemContext() {
      return !!this.item;
    },
    householdSize() {
      return this.item?.household_size || this.peopleCount;
    },
    portionDistribution() {
      return Array.isArray(this.item?.portion_distribution) ? this.item.portion_distribution : [];
    },
    personalizedIngredients() {
      return Array.isArray(this.item?.personalized_ingredients) ? this.item.personalized_ingredients : [];
    },
    portionColors() {
      return ["#52782f", "#9fbd68", "#d7b45b", "#7d9db7", "#b47d9d"];
    },
    pieBackground() {
      if (!this.portionDistribution.length) return "transparent";
      let cursor = 0;
      const parts = this.portionDistribution.map((member, index) => {
        const start = cursor;
        cursor += Number(member.share || 0);
        return this.portionColors[index % this.portionColors.length] + " " + start + "% " + cursor + "%";
      });
      return "conic-gradient(" + parts.join(", ") + ")";
    },
    ingredients() {
      const HIDDEN = ["Poivre noir, poudre", "Sel blanc alimentaire"];

      const filterHidden = (list) =>
        list.filter((ing) => {
          const name = ing?.aliment?.display_name || ing?.aliment?.name || ing?.name || "";
          return !HIDDEN.includes(name);
        });

      if (Array.isArray(this.adjustedIngredients)) {
        return filterHidden(this.adjustedIngredients);
      }

      if (Array.isArray(this.item?.ajusted_ingredients)) {
        return filterHidden(this.item.ajusted_ingredients);
      }

      if (Array.isArray(this.recipe?.dish_lines)) {
        return filterHidden(this.recipe.dish_lines);
      }

      return [];
    },
    dateFromRoute() {
      return this.$route.params.date;
    },
    macroLabels() {
      if (!this.recipe?.macro_nutriments) return [];
      const map = { P: "Protéines", L: "Lipides", G: "Glucides" };
      return this.recipe.macro_nutriments.split("-").map((letter) => map[letter] || letter);
    },
  },

  methods: {
    humanizeGlycemicIndex(g) {
      if (g === "low") return "Index glycémique bas";
      if (g === "medium") return "Index glycémique moyen";
      return "Index glycémique élevé";
    },
    glycemicIndexLabel(g) {
      if (g === "low") return "Bas";
      if (g === "medium") return "Moyen";
      return "Élevé";
    },
    humanizeMacroNutriments(value) {
      if (!value) return "";
      const map = { P: "Protéines", L: "Lipides", G: "Glucides" };
      return value.split("-").map(l => map[l] || l).join(" - ");
    },

    sportLoadLabel(level) {
      return {
        light: "charge légère",
        moderate: "charge modérée",
        high: "forte charge",
        competition: "compétition",
      }[level] || level;
    },

    ingredientName(ingredient) {
      return ingredient?.aliment?.display_name || ingredient?.aliment?.name || ingredient?.name || "";
    },

    emitUpdatedRecipe(recipePatch) {
      if (this.hasMenuItemContext) {
        this.$emit("update", {
          ...this.item,
          dish: recipePatch,
        });
        return;
      }

      this.$emit("update", recipePatch);
    },

    getReviewStarIcon(i, rate) {
      if (this.hoveredStar >= i) return ["fas", "star"];
      return rate >= i ? ["fas", "star"] : ["far", "star"];
    },
    hoverStar(i) {
      this.hoveredStar = i;
    },
    resetHover() {
      this.hoveredStar = 0;
    },

    selectStar(i) {
      api.post(`/recipes/${this.recipe.id}/rate/`, { rating: i }).then((res) => {
        this.emitUpdatedRecipe({ ...this.recipe, my_rate: res.data.my_rate });
      });
    },

    saveMyReview() {
      api.post(`/recipes/${this.recipe.id}/comment/`, { comment: this.myReview }).then((res) => {
        this.emitUpdatedRecipe({ ...this.recipe, my_comment: res.data.my_comment });
        this.reviewEditionMode = false;
      });
    },

    editReview() {
      this.myReview = this.recipe.my_comment.comment;
      this.reviewEditionMode = true;
    },

    toggleLike() {
      api.post(`/recipes/${this.recipe.id}/toggle_like/`).then((res) => {
        this.emitUpdatedRecipe({
          ...this.recipe,
          liked: !this.recipe.liked,
          nb_likes: res.data.nb_likes,
        });
      });
    },

    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },

    updateRecipe(newItem) {
      this.$emit("update", this.hasMenuItemContext ? newItem : newItem?.dish || this.recipe);
      this.showReplaceModal = false;
    },
  },
};
</script>

<style scoped>
.dish-card {
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;
  gap: 22px;
  padding: 18px;
}

.dish-main {
  flex: 1;
  min-width: 0;
}

.dish-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}

.dish-type {
  color: #789b4a;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.dish-title {
  margin-top: 3px;
  color: #3c6610;
  font-size: clamp(24px, 2.2vw, 36px);
  font-weight: 800;
  line-height: 1.08;
}

.dish-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.replace-button {
  border: 0;
  border-radius: 999px;
  padding: 9px 14px;
  background: #52782f;
  color: white;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.quick-info {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 13px;
}

.info-chip {
  min-height: 48px;
  padding: 7px 11px;
  border: 1px solid #dbe7cd;
  border-radius: 10px;
  background: #f7faF2;
  color: #426426;
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 800;
}

.info-chip small {
  display: block;
  margin-bottom: 1px;
  color: #7a8870;
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.macro-profile {
  margin-bottom: 13px;
  padding: 11px 13px;
  border-radius: 12px;
  background: #f5f8ef;
  border: 1px solid #dbe7cd;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.macro-profile strong {
  color: #365c1f;
  font-size: 12px;
}

.macro-profile p {
  margin: 2px 0 0;
  color: #73806c;
  font-size: 10px;
}

.macro-badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.macro-badges span {
  padding: 5px 8px;
  border-radius: 999px;
  background: white;
  border: 1px solid #cadbb8;
  color: #456c2a;
  font-size: 10px;
  font-weight: 800;
}

.quantity-context {
  margin: 8px 0 10px;
  padding: 9px 12px;
  border-left: 3px solid #789b4a;
  background: #fbfcf8;
}

.quantity-context strong {
  display: block;
  color: #365c1f;
  font-size: 12px;
}

.quantity-context span {
  color: #6f796a;
  font-size: 10px;
}

 .portion-card {
  margin: 12px 0 16px;
  padding: 14px 16px;
  border: 1px solid #dbe7cd;
  border-radius: 14px;
  background: #f8fbf3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}
.portion-copy > strong { display: block; color: #365c1f; font-size: 14px; }
.portion-copy > span { color: #6f796a; font-size: 10px; }
.portion-legend { margin-top: 9px; display: flex; flex-wrap: wrap; gap: 8px 14px; }
.portion-person { font-size: 11px; color: #53604d; display: flex; align-items: center; gap: 5px; }
.portion-person i { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.portion-pie { width: 92px; height: 92px; border-radius: 50%; flex: 0 0 92px; }

@media (max-width: 900px) {
  .dish-card { flex-direction: column; }
  .menu-item-image-container { width: 100% !important; height: 260px; margin-right: 0 !important; }
  .dish-header, .macro-profile { flex-direction: column; }
  .dish-actions { align-self: stretch; }
}

.menu-item-image-container {
  width: 200px;
  object-fit: cover;
  margin-right: 20px;
  border-radius: 10px;
  position: relative;
}

.menu-item-image {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  object-fit: cover;
}

.menu-item-rate {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 30px;
  height: 30px;
  background: gold;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.menu-item-name {
  font-size: 20px;
  font-weight: bold;
  color: #3c6610;
}

.menu-item-ingredients {
  font-size: 14px;
}


.personalized-breakdown {
  margin: 14px 0 18px;
  border: 1px solid #dfe8d4;
  border-radius: 14px;
  background: #fbfdf8;
  overflow: hidden;
}
.personalized-breakdown summary {
  padding: 12px 14px;
  cursor: pointer;
  color: #315b19;
  font-weight: 800;
}
.personalized-grid {
  display: grid;
  gap: 10px;
  padding: 0 14px 14px;
}
.personalized-member {
  padding: 12px;
  border: 1px solid #e3eadc;
  border-radius: 11px;
  background: #fff;
}
.personalized-member-title {
  display: flex;
  gap: 10px;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 7px;
}
.personalized-member-title strong { color: #263322; }
.personalized-member-title span { color: #52782f; font-size: 11px; font-weight: 800; }
.personalized-member ul {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #56604f;
}
.sport-supplement {
  display: grid;
  gap: 2px;
  margin-top: 9px;
  padding: 9px 10px;
  border-radius: 9px;
  background: #eef6e4;
  color: #315b19;
}
.sport-supplement span { font-weight: 800; }
.sport-supplement small { color: #66705f; }

.menu-item-recipe {
  font-size: 14px;
}

.tag {
  background: #b5d088;
  color: white;
  padding: 4px 12px;
  border-radius: 5px;
  display: inline-block;
}

.star {
  font-size: 20px;
  cursor: pointer;
  transition: color 0.3s ease;
  color: gold;
}

.star:hover {
  color: gold;
}

.review {
  align-items: center;
  justify-content: space-between;
  display: flex;
  border-radius: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 16px;
  max-width: 500px;
}
</style>
